import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';

const CURRENCIES = ['IDR', 'EUR', 'USD'] as const;
type Opt = { id: string; name: string };
type Line = { id?: string; offer_id: string | null; asset_id: string | null; description: string; quantity: string; unit_price: string };
export type QuoteRow = { id: string; client_id: string | null; property_id: string | null; title: string | null; currency: string; valid_until: string | null; terms: string | null };
type Base<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };

export function QuoteBuilder({ open, onClose, onSaved, initial }: Base<QuoteRow>) {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency: orgCur } = useOrg();
  const [clients, setClients] = useState<Opt[]>([]);
  const [properties, setProperties] = useState<(Opt & { client_id: string | null })[]>([]);
  const [offers, setOffers] = useState<Opt[]>([]);
  const [assets, setAssets] = useState<(Opt & { property_id: string })[]>([]);
  const [f, setF] = useState({ client_id: '', property_id: '', title: '', currency: orgCur, valid_until: '', terms: '' });
  const [lines, setLines] = useState<Line[]>([]);
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return; setErr({});
    (async () => {
      const [cl, pr, of, as] = await Promise.all([
        supabase.from('clients').select('id,name').is('deleted_at', null).order('name'),
        supabase.from('properties').select('id,name,client_id').is('deleted_at', null).order('name'),
        supabase.from('catalogue_offers').select('id,name').is('deleted_at', null).eq('active', true).order('name'),
        supabase.from('assets').select('id,name,property_id').is('deleted_at', null).order('name'),
      ]);
      setClients((cl.data as Opt[]) ?? []); setProperties((pr.data as any[]) ?? []); setOffers((of.data as Opt[]) ?? []); setAssets((as.data as any[]) ?? []);
      setF({ client_id: initial?.client_id ?? '', property_id: initial?.property_id ?? '', title: initial?.title ?? '', currency: initial?.currency ?? orgCur, valid_until: initial?.valid_until ?? '', terms: initial?.terms ?? '' });
      if (initial?.id) {
        const ln = await supabase.from('quote_lines').select('id,offer_id,asset_id,description,quantity,unit_price').eq('quote_id', initial.id).is('deleted_at', null).order('position');
        setLines(((ln.data as any[]) ?? []).map((l) => ({ id: l.id, offer_id: l.offer_id, asset_id: l.asset_id, description: l.description, quantity: String(l.quantity), unit_price: String(l.unit_price) })));
      } else setLines([]);
    })();
  }, [open, initial]);

  function addLine() { setLines([...lines, { offer_id: null, asset_id: null, description: '', quantity: '1', unit_price: '' }]); }
  function upd(i: number, patch: Partial<Line>) { setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l))); }

  async function pickOffer(i: number, offerId: string) {
    const offer = offers.find((o) => o.id === offerId);
    const patch: Partial<Line> = { offer_id: offerId || null, asset_id: null, description: offer?.name ?? lines[i]?.description ?? '' };
    if (offerId && f.property_id) {
      const { data } = await supabase.rpc('price_offer', { p_offer: offerId, p_currency: f.currency, p_property: f.property_id });
      if (data != null) patch.unit_price = String(data);
    }
    upd(i, patch);
  }

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const valid = lines.filter((l) => l.description.trim());
    if (!valid.length) { setErr({ _: t('quote.needLine') }); return; }
    setBusy(true);
    const head = { client_id: f.client_id || null, property_id: f.property_id || null, title: f.title.trim() || null, currency: f.currency, valid_until: f.valid_until || null, terms: f.terms.trim() || null };
    const saved = initial?.id
      ? await supabase.from('quotes').update(head).eq('id', initial.id).select('id').single()
      : await supabase.from('quotes').insert({ ...head, organization_id: orgId } as any).select('id').single();
    if (saved.error) { setBusy(false); setErr({ _: dbErrorMessage(t, saved.error) }); return; }
    const quoteId = (saved.data as any).id;
    const existing = await supabase.from('quote_lines').select('id').eq('quote_id', quoteId).is('deleted_at', null);
    const keep = new Set(valid.map((l) => l.id).filter(Boolean));
    for (const ex of ((existing.data as any[]) ?? [])) if (!keep.has(ex.id)) await supabase.from('quote_lines').delete().eq('id', ex.id);
    let pos = 0;
    for (const l of valid) {
      const row = { organization_id: orgId, quote_id: quoteId, offer_id: l.offer_id, asset_id: l.asset_id, description: l.description.trim(), quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0, position: pos++ };
      if (l.id) await supabase.from('quote_lines').update(row).eq('id', l.id);
      else await supabase.from('quote_lines').insert(row as any);
    }
    setBusy(false); onSaved(); onClose();
  }

  const propAssets = assets.filter((a) => !f.property_id || a.property_id === f.property_id);

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('quote.edit') : t('quote.new')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('nav.clients')}>
            <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}><option value="">—</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
          </Field>
          <Field label={t('nav.properties')}>
            <Select value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })}><option value="">—</option>{properties.filter((p) => !f.client_id || p.client_id === f.client_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
          </Field>
          <Field label={t('quote.title')}><TextInput value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
          <Field label={t('cat.currency')}><Select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
          <Field label={t('quote.validUntil')}><TextInput type="date" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} /></Field>
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          <div className="flex items-center justify-between"><span className="text-xs font-medium text-muted">{t('quote.lines')}</span><Button type="button" variant="ghost" size="sm" onClick={addLine}>+ {t('cost.add')}</Button></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_70px_110px_auto] items-center gap-2">
              <Select value={l.offer_id ?? ''} onChange={(e) => pickOffer(i, e.target.value)}><option value="">{t('quote.catalogue')}</option>{offers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select>
              <TextInput value={l.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder={t('cost.description')} />
              <TextInput type="number" min="0" step="0.5" value={l.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} />
              <TextInput type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => upd(i, { unit_price: e.target.value })} placeholder={t('cat.unitPrice')} />
              <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="px-2 text-muted hover:text-st-overdue-fg">✕</button>
            </div>
          ))}
          <div className="mt-1 flex items-center gap-2">
            <Select className="w-48" value="" onChange={(e) => { if (e.target.value) { const a = propAssets.find((x) => x.id === e.target.value); setLines([...lines, { offer_id: null, asset_id: e.target.value, description: a?.name ?? '', quantity: '1', unit_price: '' }]); } }}>
              <option value="">+ {t('quote.fromInventory')}</option>{propAssets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
            <span className="ml-auto font-mono text-sm font-semibold">{t('cost.total')}: {formatCurrency(total, f.currency, i18n.language)}</span>
          </div>
        </div>

        <Field label={t('quote.terms')}><Textarea value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
